import { LitElement, html, css } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";

/**
 * Detects the current tenant UI type by inspecting loaded stylesheets.
 * Falls back to 'antilles' for unknown tenants.
 * Uses stylesheet link paths since the activation URL has no tenant prefix.
 *
 * @returns {'legadom'|'antilles'}
 */
function detectUIType() {
	const links = document.querySelectorAll('link[rel="stylesheet"]');
	for (const link of links) {
		const href = link.getAttribute('href') || '';
		if (href.includes('/legadom/')) return 'legadom';
	}
	return 'antilles';
}

/**
 * Returns the theme color tokens for the given UI type.
 * @param {'legadom'|'antilles'} uiType
 */
function getTheme(uiType) {
	if (uiType === 'legadom') {
		return {
			headerBg:    '#111955',
			headerColor: '#ffffff',
			btnBg:       '#fd5717',
			btnHover:    '#e04a10',
			btnColor:    '#ffffff',
			borderColor: '#111955',
			accentColor: '#111955',
		};
	}
	// antilles + guyane
	return {
		headerBg:    '#03879e',
		headerColor: '#ffffff',
		btnBg:       '#fd5717',
		btnHover:    '#e04a10',
		btnColor:    '#ffffff',
		borderColor: '#03879e',
		accentColor: '#03879e',
	};
}

/**
 * <alert-modal> — A reusable alert/notification dialog web component.
 *
 * Usage:
 *   HTML:  <alert-modal id="myAlert"></alert-modal>
 *   JS:    document.getElementById('myAlert').show('Your message here');
 *
 * The component auto-detects the active tenant theme (Antilles/Guyane vs LegaDom)
 * from the page's loaded stylesheets.
 *
 * @fires alert-closed - dispatched when the user dismisses the dialog
 */
export class AlertModal extends LitElement {

	static properties = {
		message: { type: String },
		items:   { type: Array  },
		open:    { type: Boolean, reflect: true }
	};

	constructor() {
		super();
		this.message = '';
		this.items   = [];
		this.open    = false;
		this._theme  = getTheme(detectUIType());
	}

	static get styles() {
		return css`
			:host {
				display: none;
			}
			:host([open]) {
				display: block;
			}

			/* ── Backdrop ── */
			.backdrop {
				position: fixed;
				inset: 0;
				background: rgba(0, 0, 0, 0.45);
				display: flex;
				align-items: center;
				justify-content: center;
				z-index: 9999;
				animation: fadeIn 0.18s ease;
			}

			/* ── Card ── */
			.card {
				background: #ffffff;
				border-radius: 16px;
				box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
				max-width: 420px;
				width: 90%;
				overflow: hidden;
				animation: slideDown 0.2s ease;
			}

			/* ── Header ── */
			.header {
				display: flex;
				align-items: center;
				gap: 10px;
				padding: 18px 24px;
				background: var(--alert-header-bg, #03879e);
				color: var(--alert-header-color, #ffffff);
			}
			.header-icon {
				flex-shrink: 0;
				width: 26px;
				height: 26px;
				background: rgba(255,255,255,0.25);
				border-radius: 50%;
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 15px;
				font-weight: 700;
				line-height: 1;
			}
			.header-title {
				font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif;
				font-size: 1.05rem;
				font-weight: 700;
				letter-spacing: 0.02em;
				margin: 0;
			}

			/* ── Body ── */
			.body {
				padding: 22px 24px 8px;
				font-family: 'Segoe UI', Arial, sans-serif;
				font-size: 0.97rem;
				color: #333;
				line-height: 1.55;
				min-height: 40px;
			}

			/* ── Footer ── */
			.footer {
				padding: 12px 24px 20px;
				display: flex;
				justify-content: flex-end;
			}
			.btn-ok {
				background: var(--alert-btn-bg, #fd5717);
				color: var(--alert-btn-color, #ffffff);
				border: none;
				border-radius: 30px;
				padding: 10px 36px;
				font-size: 0.9rem;
				font-weight: 700;
				font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif;
				letter-spacing: 0.06em;
				text-transform: uppercase;
				cursor: pointer;
				transition: background 0.15s ease, transform 0.1s ease;
			}
			.btn-ok:hover {
				background: var(--alert-btn-hover, #e04a10);
			}
			.btn-ok:active {
				transform: scale(0.97);
			}

			/* ── Missing-fields list ── */
			.items-list {
				margin: 6px 0 0;
				padding-left: 20px;
				font-family: 'Segoe UI', Arial, sans-serif;
				font-size: 0.93rem;
				color: #444;
				line-height: 1.7;
			}
			.items-list li {
				margin-bottom: 1px;
			}

			/* ── Animations ── */
			@keyframes fadeIn {
				from { opacity: 0; }
				to   { opacity: 1; }
			}
			@keyframes slideDown {
				from { transform: translateY(-20px); opacity: 0; }
				to   { transform: translateY(0);     opacity: 1; }
			}
		`;
	}

	/**
	 * Opens the modal with the given message.
	 * @param {string} message
	 */
	show(message, items) {
		this.message = message;
		this.items   = Array.isArray(items) ? items : [];
		this.open    = true;
	}

	_close() {
		this.open = false;
		this.dispatchEvent(new CustomEvent('alert-closed', { bubbles: true, composed: true }));
	}

	render() {
		const t = this._theme;

		return html`
			<style>
				.header {
					background: ${t.headerBg} !important;
					color: ${t.headerColor} !important;
				}
				.btn-ok {
					background: ${t.btnBg} !important;
					color: ${t.btnColor} !important;
				}
				.btn-ok:hover {
					background: ${t.btnHover} !important;
				}
			</style>

			<div class="backdrop" @click="${this._backdropClick}">
				<div class="card" role="alertdialog" aria-modal="true" aria-labelledby="alert-title" aria-describedby="alert-message">
					<div class="header">
						<div class="header-icon">!</div>
						<p id="alert-title" class="header-title">Attention</p>
					</div>
					<div class="body">
						<p id="alert-message">${this.message}</p>
						${this.items.length > 0 ? html`
							<ul class="items-list">
								${this.items.map(item => html`<li>${item}</li>`)}
							</ul>` : ''}
					</div>
					<div class="footer">
						<button class="btn-ok" @click="${this._close}" autofocus>OK</button>
					</div>
				</div>
			</div>
		`;
	}

	/** Close only when clicking the backdrop itself, not the card. */
	_backdropClick(e) {
		if (e.target === e.currentTarget) {
			this._close();
		}
	}

	/** Allow closing with Escape key. */
	connectedCallback() {
		super.connectedCallback();
		this._keyHandler = (e) => { if (e.key === 'Escape' && this.open) this._close(); };
		document.addEventListener('keydown', this._keyHandler);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('keydown', this._keyHandler);
	}
}

customElements.define('alert-modal', AlertModal);
